import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, describeApiError } from '../lib/api';
import { MERGE_FIELDS, mailtoHref } from '../../../shared/comms';

interface RenderedTemplate { id: string; name: string; subject: string; body: string; missing: string[]; unknown: string[] }
interface CommsResponse { data: { to: string[]; templates: RenderedTemplate[] } }

const fieldLabel = (k: string) => MERGE_FIELDS.find((f) => f.key === k)?.label ?? k;

/**
 * Write to the client from a practice template merged with the record. Client-Base does
 * not send: the draft opens in the adviser's mail app (or is copied for a letter), then
 * "Log as sent" files a note on the record.
 */
export function WritePanel({ clientId, canEdit, onClose, onLogged }: { clientId: string; canEdit: boolean; onClose: () => void; onLogged: (msg: string) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['client', clientId, 'comms'], queryFn: () => api<CommsResponse>(`/api/clients/${clientId}/comms`) });
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const templates = useMemo(() => q.data?.data.templates ?? [], [q.data]);
  const to = useMemo(() => q.data?.data.to ?? [], [q.data]);
  const current = templates.find((t) => t.id === templateId);

  // Pick a template: its merged text replaces the draft (first one on load).
  function choose(id: string) {
    const t = templates.find((x) => x.id === id);
    setTemplateId(id);
    if (t) { setSubject(t.subject); setBody(t.body); }
  }
  useEffect(() => {
    if (!templateId && templates.length) {
      setTemplateId(templates[0].id); setSubject(templates[0].subject); setBody(templates[0].body);
    }
  }, [templates, templateId]);

  async function log(channel: 'email' | 'letter') {
    setBusy(true); setMsg(null);
    try {
      await api(`/api/clients/${clientId}/comms/log`, {
        method: 'POST', body: JSON.stringify({ templateId: templateId || undefined, subject, body, to: channel === 'email' ? to : [], channel }),
      });
      void qc.invalidateQueries({ queryKey: ['client', clientId] });
      onLogged(channel === 'email' ? `Logged the email "${subject}" as a file note.` : `Logged the letter "${subject}" as a file note.`);
      onClose();
    } catch (e) { setMsg(describeApiError(e)); } finally { setBusy(false); }
  }

  async function copy() {
    try { await navigator.clipboard.writeText(`${subject}\n\n${body}`); setMsg('Copied.'); } catch { setMsg('Copy is not available here; select the text instead.'); }
  }

  return (
    <section className="qa-card p-4" aria-label="Write to client" data-testid="write-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <p className="qa-eyebrow">Write to client</p>
        <button type="button" className="btn-ghost text-xs" onClick={onClose}>Close</button>
      </div>
      {q.isLoading ? <p className="text-sm text-ink-mute">Loading templates...</p> : q.isError ? <p className="text-sm text-rust">Could not load templates.</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-xs">
          <label>Template
            <select className="qa-input block w-full mt-1 text-sm" aria-label="Template" value={templateId} onChange={(e) => choose(e.target.value)}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <div className="lg:col-span-2">To
            <p className="qa-mono text-sm mt-2 truncate" data-testid="write-to">{to.length ? to.join(', ') : 'No email address on the record (log it as a letter instead)'}</p>
          </div>
          <label className="lg:col-span-3">Subject<input className="qa-input block w-full mt-1 text-sm" aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></label>
          <label className="lg:col-span-3">Message<textarea className="qa-input block w-full mt-1 text-sm" rows={9} aria-label="Message" value={body} onChange={(e) => setBody(e.target.value)} /></label>
          {current && current.missing.length > 0 && (
            <p className="lg:col-span-3 text-xs text-rust" role="note">Not on the record, left blank: {current.missing.map(fieldLabel).join('; ')}.</p>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        <a className={`btn-primary text-sm ${!to.length || !subject.trim() ? 'pointer-events-none opacity-50' : ''}`} href={to.length ? mailtoHref(to, subject, body) : undefined}
          aria-disabled={!to.length || !subject.trim()}>Open in mail app</a>
        <button type="button" className="btn-ghost text-xs" onClick={copy}>Copy text</button>
        {canEdit && <button type="button" className="btn-ghost text-sm" disabled={busy || !subject.trim() || !to.length} onClick={() => log('email')}>Log as sent</button>}
        {canEdit && <button type="button" className="btn-ghost text-xs" disabled={busy || !subject.trim()} onClick={() => log('letter')}>Log as letter</button>}
      </div>
      {msg && <p role="status" className="text-xs text-ink-soft mt-2">{msg}</p>}
      <p className="text-[11px] text-ink-mute mt-2">Client-Base does not send. Templates are set by an admin under Today, Practice settings.</p>
    </section>
  );
}
